import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import { NOTIFICATION_AUDIENCE_ROLES } from '../config/roleConfig';
import useLanguage from '../hooks/useLanguage';
import { notificationApi } from '../services/notificationApi';
import {
  pushSupported,
  permissionState,
  isIos,
  isStandalone,
  subscribeThisBrowser,
  unsubscribeThisBrowser,
  getStoredDeviceId,
  setStoredDeviceId
} from '../utils/serviceWorker';
import {
  typeLabel,
  deviceHealth,
  permissionLabel,
  subscribeFailureMessage,
  testSentMessage,
  messageForError
} from '../config/notificationLabels';
import { formatCount } from '../utils/numberFormatter';
import { formatRelativeTime } from '../utils/dateFormatter';
import '../styles/NotificationSettings.css';

/**
 * Push notification settings — Guest Comms stage 1, Inc 5b (pass 2). D78, D79, D87, D91.
 *
 * WHY THIS SCREEN EXISTS AT ALL, in one sentence: **every way a push subscription dies is silent**
 * (D87 — *"this is actually the hardest problem in the whole notification design. Not delivery.
 * Subscriptions."*). People clear storage, replace phones, revoke permission, delete home-screen
 * icons. None of those tell anybody. So the panel is deliberately status-first: the top card answers
 * «هل سيصلني تنبيه؟» before offering a single control, and every device carries its own visible
 * confidence — the difference between "my phone is quiet for a reason I can name" and a mystery.
 *
 * ⚠️ BILINGUAL SINCE 2026-08-04, and it was Arabic-only before that for a reason that has now
 * expired. The old note read: *"English is scoped to the cleaning/service crews and this surface is
 * core-staff, so no English-locale user reaches it."* The `guest_checkout` type
 * (tasks/manual-guest-checkout-event.md D31/D32) is addressed to the cleaning crew, so the crew
 * reaches this panel — and the crew is precisely who English exists for. Copy now lives in
 * `locales/{ar,en}.js` under `notifications.*`; `config/notificationLabels.js` kept only the mapping
 * from backend keys and error codes to locale keys.
 *
 * ⚠️ NO BOOTSTRAP LOGICAL CLASSES (`ms-*`, `me-*`, `text-end`). The app loads the RTL BUILD of
 * Bootstrap globally, so those resolve to the wrong side wherever direction is not RTL. Spacing is
 * real CSS logical properties, in NotificationSettings.css (I18N_STRATEGY.md). This matters more
 * now than it did: before, "wherever direction is not RTL" was hypothetical on this screen.
 *
 * ⚠️ COUNTS GO THROUGH `formatCount`. Digits joined by a separator form one left-to-right run, so a
 * hand-written counter renders backwards to an Arabic reader.
 */
const NotificationSettingsPanel = () => {
  const { user } = useContext(AuthContext);
  const { t, isArabic, language } = useLanguage();
  // Everyone a registered notification type can address — core staff plus the cleaning crew since
  // `guest_checkout` (D31/D34). Mirrors the backend's registry-derived router gate; the backend is
  // still the enforcement point and 403s anyone else regardless.
  const inAudience = NOTIFICATION_AUDIENCE_ROLES.includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);          // { enabled, configured, vapidPublicKey, types }
  const [devices, setDevices] = useState([]);
  const [preferences, setPreferences] = useState({});
  const [types, setTypes] = useState([]);

  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // The read itself failed — distinct from "push is off", which is a fact the server told us.
  const [unreadable, setUnreadable] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // The browser's own permission, held in state because it changes as a RESULT of what happens here
  // and nothing re-renders on its own when it does.
  const [permission, setPermission] = useState(permissionState());
  const [thisDeviceId, setThisDeviceId] = useState(getStoredDeviceId());

  const supported = pushSupported();

  /**
   * Show the Add-to-Home-Screen guidance ONLY where it is both true and actionable.
   *
   * ⚠️ THE CAPABILITY CHECK IS LOAD-BEARING, not belt-and-braces. An earlier version tested the
   * user agent alone (`isIos() && !isStandalone()`) and put the install card on a DESKTOP machine,
   * where it is simply wrong and unactionable — reported 2026-08-01. A UA is a guess: iPadOS reports
   * a Macintosh string in desktop mode, so telling an iPad from a Mac means falling back to touch
   * heuristics that other devices also satisfy.
   *
   * `!supported` is not a guess. The card exists to answer exactly one question — "why can I not
   * turn notifications on?" — and on iOS the answer is "Safari withholds the Push API until the site
   * is on the Home Screen". So the honest condition is **push is genuinely unavailable AND
   * installing is what would fix it**, which no desktop browser can satisfy because every one of
   * them already supports push.
   */
  const iosNeedsInstall = !supported && isIos() && !isStandalone();

  /** My devices + preferences. Only ever called when the flag is on — otherwise every route 403s. */
  const loadMe = useCallback(async () => {
    const data = await notificationApi.me();
    setDevices(data.devices || []);
    setPreferences(data.preferences || {});
    setTypes(data.types || []);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const cfg = await notificationApi.config();
        if (cancelled) return;
        setConfig(cfg);
        // Reading `/me` while the flag is off would be a guaranteed 403 rendered as an error the
        // operator can do nothing about. «غير مفعّل» is the honest answer and `/config` gives it.
        if (cfg.enabled) await loadMe();
      } catch (err) {
        if (cancelled) return;
        // A non-core-staff account 403s at the router. That is correct and not an error worth
        // showing — the panel simply does not apply to them, and `inAudience` hides it anyway.
        if (err?.response?.status === 403 && !inAudience) return;
        console.error('Failed to load notification settings:', err);
        setError(messageForError(t, err));
        // ⚠️ NOT KNOWING IS NOT THE SAME AS OFF. Without this, a failed read left `config` null and
        // the status card confidently announced «غير مفعّلة في هذه البيئة — لا حاجة لأي إجراء منك»
        // to someone whose notifications may be perfectly configured. On the one screen that exists
        // to diagnose silence (D87), that is the worst possible answer. `InboxPage` already learned
        // this lesson and words its equivalent state "unavailable", never "not enabled".
        setUnreadable(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [loadMe, inAudience]); // eslint-disable-line react-hooks/exhaustive-deps -- `t` is recreated every render; including it would re-run the load on each one

  /**
   * Turn notifications on for THIS browser.
   *
   * The whole sequence runs inside this click: browsers require a real user gesture for a permission
   * prompt, and the ones that do not still punish an unprompted ask with a permanent `denied` that
   * no code can undo.
   */
  const handleEnable = async () => {
    setEnabling(true);
    setError('');
    setNotice('');
    try {
      const result = await subscribeThisBrowser(config?.vapidPublicKey);
      setPermission(permissionState());

      if (!result.ok) {
        setError(subscribeFailureMessage(t, result.reason));
        return;
      }

      const saved = await notificationApi.subscribe(result.subscription);
      // Kept so a later notification tap can be attributed to THIS handset without the push
      // endpoint — a bearer capability — ever coming back to the client.
      setStoredDeviceId(saved.deviceId);
      setThisDeviceId(saved.deviceId || null);

      await loadMe();
      setNotice(t('notifications.enabledNotice'));
    } catch (err) {
      console.error('Enable notifications failed:', err);
      setError(messageForError(t, err));
    } finally {
      setEnabling(false);
    }
  };

  /** «أرسل لي إشعارًا تجريبيًا» — the D91 gate: everyone installs, receives, and taps, once. */
  const handleTest = async () => {
    setTesting(true);
    setError('');
    setNotice('');
    try {
      const result = await notificationApi.test();
      // Arabic marks one / two / many differently — see testSentMessage. A count-plus-noun template
      // renders «إلى ١ من أجهزتك», which is the most common case here and is not idiomatic.
      setNotice(testSentMessage(t, result.accepted, (n) => formatCount(n, language)));
      // The send stamps device health server-side, so the list is stale the moment it returns.
      await loadMe();
    } catch (err) {
      console.error('Test notification failed:', err);
      setError(messageForError(t, err));
      // A failure is ALSO news about the devices — a 410 just deleted one. Refresh either way.
      loadMe().catch(() => {});
    } finally {
      setTesting(false);
    }
  };

  /**
   * Remove one device. Reversible by pressing «تفعيل الإشعارات» again, so no confirmation step —
   * a modal here would be ceremony over a mistake that costs one tap to undo.
   */
  const handleRemove = async (deviceId) => {
    setRemovingId(deviceId);
    setError('');
    setNotice('');
    try {
      await notificationApi.removeDevice(deviceId);
      // Removing the device you are HOLDING must also drop the browser's half of the pairing, or the
      // browser keeps a live subscription with no row behind it and «تفعيل» would short-circuit on
      // it rather than registering anew.
      if (String(deviceId) === String(thisDeviceId)) {
        await unsubscribeThisBrowser();
        setStoredDeviceId(null);
        setThisDeviceId(null);
      }
      await loadMe();
      setNotice(t('notifications.removedNotice'));
    } catch (err) {
      console.error('Remove device failed:', err);
      setError(messageForError(t, err));
    } finally {
      setRemovingId(null);
    }
  };

  /**
   * Toggle one type for me. Painted optimistically and ROLLED BACK on failure: a switch that waits
   * for a round trip feels broken, but one that stays flipped after the server refused is a lie the
   * operator only discovers when a notification does not arrive.
   */
  const handleToggle = async (type, enabled) => {
    setPreferences((prev) => ({ ...prev, [type]: enabled }));
    setError('');
    try {
      await notificationApi.setPreference(type, enabled);
    } catch (err) {
      console.error('Preference update failed:', err);
      setPreferences((prev) => ({ ...prev, [type]: !enabled }));
      setError(messageForError(t, err));
    }
  };

  // Defence in depth only — the router is the enforcement point and 403s any role outside every
  // type's audience regardless (owners joined the audience 2026-10-05, owner booking pushes).
  if (!inAudience) return null;

  if (loading) {
    return (
      <div className="notif-section">
        <Spinner animation="border" size="sm" /> <span>{t('notifications.loading')}</span>
      </div>
    );
  }

  // ---- The status card: what state are we actually in, and what is the ONE next action ----

  const registeredHere = devices.find((d) => String(d._id) === String(thisDeviceId));
  const healthyHere = registeredHere && registeredHere.health === 'ok';

  let status = { tone: 'off', icon: '🔕', title: t('notifications.status.disabledTitle'), detail: '', action: null };

  if (unreadable) {
    // Says only what is true: we could not read the state. Never "no action needed".
    status = {
      tone: 'attention',
      icon: '⚠️',
      title: t('notifications.status.unreadableTitle'),
      detail: t('notifications.status.unreadableDetail'),
      action: null
    };
  } else if (!config?.enabled) {
    status = {
      tone: 'off',
      icon: '🔕',
      title: t('notifications.status.disabledTitle'),
      detail: t('notifications.status.disabledDetail'),
      action: null
    };
  } else if (!config?.configured) {
    // A server-side problem the operator cannot fix, said plainly rather than dressed as their
    // mistake. This is the state the pass-1 audit built `warnUnconfigured()` for.
    status = {
      tone: 'attention',
      icon: '⚠️',
      title: t('notifications.status.unconfiguredTitle'),
      detail: t('notifications.status.unconfiguredDetail'),
      action: null
    };
  } else if (iosNeedsInstall) {
    // iOS exposes the Push API only to a page added to the Home Screen. Not promptable by any code,
    // on any browser — a manual per-device step (D78's platform reality).
    status = {
      tone: 'attention',
      icon: '📲',
      title: t('notifications.status.iosInstallTitle'),
      detail: t('notifications.status.iosInstallDetail'),
      action: null
    };
  } else if (!supported) {
    status = {
      tone: 'attention',
      icon: '⚠️',
      title: t('notifications.status.unsupportedTitle'),
      detail: t('notifications.status.unsupportedDetail'),
      action: null
    };
  } else if (permission === 'denied') {
    // Asking again resolves instantly with no prompt, so a retry button would appear to do nothing.
    status = {
      tone: 'attention',
      icon: '🚫',
      title: t('notifications.status.deniedTitle'),
      detail: t('notifications.status.deniedDetail'),
      action: null
    };
  } else if (healthyHere) {
    status = {
      tone: 'on',
      icon: '🔔',
      title: t('notifications.status.onTitle'),
      detail: t('notifications.status.onDetail'),
      action: 'test'
    };
  } else if (registeredHere?.health === 'stale') {
    // Registered, but bound to a different VAPID key — cryptographically dead and quiet forever.
    // Naming it is the entire point of storing `vapidKeyFingerprint` (R11).
    status = {
      tone: 'attention',
      icon: '⚠️',
      title: t('notifications.status.staleTitle'),
      detail: t('notifications.status.staleDetail'),
      action: 'enable'
    };
  } else if (registeredHere) {
    // `health: 'unknown'` — a row with no fingerprint, written before that column existed or while
    // the transport was unconfigured. Split out from the branch above because that one ASSERTS a
    // cause ("registered with old settings") which this row is precisely the case we cannot evidence.
    // Saying so plainly, and offering the same one-tap fix, beats inventing a diagnosis.
    status = {
      tone: 'attention',
      icon: '⚠️',
      title: t('notifications.status.unknownTitle'),
      detail: t('notifications.status.unknownDetail'),
      action: 'enable'
    };
  } else {
    status = {
      tone: 'off',
      icon: '🔕',
      title: t('notifications.status.offTitle'),
      detail: t('notifications.status.offDetail'),
      action: 'enable'
    };
  }

  // The server decides which types this person may see (D34) — it filters by the role's audience,
  // so a cleaner gets one row and an operator gets three. No client-side fallback list: an empty
  // answer here means "the server says none", and inventing rows would re-create the exact
  // over-subscription the audience field exists to prevent.
  const typeList = types;

  return (
    // ⚠️ `textAlign` is set explicitly, not left to `dir`. The host Settings page hardcodes
    // `style={{ textAlign: 'right' }}` on the card body around this panel, and text-align is an
    // INHERITED PHYSICAL property — `dir="ltr"` on a child does not reset it. Without this the whole
    // newly-bilingual panel rendered right-aligned for the English-locale crew it was translated for.
    <div dir={isArabic ? 'rtl' : 'ltr'} style={{ textAlign: isArabic ? 'right' : 'left' }}>
      <div className={`notif-status notif-status--${status.tone}`}>
        <div className="notif-status-icon" aria-hidden="true">{status.icon}</div>
        <div className="notif-status-body">
          <h6 className="notif-status-title">{status.title}</h6>
          <p className="notif-status-detail">{status.detail}</p>

          {/* The browser's own permission, reported VERBATIM (D-e). The `denied` case gets its own
              status card above; this line covers the rest, so «granted» and «not asked yet» are
              visible facts rather than states the operator has to infer from which button appears. */}
          {config?.enabled && supported && permission !== 'denied' && (
            <p className="notif-status-permission">
              {t('notifications.browserPermission')}: {permissionLabel(t, permission)}
            </p>
          )}

          {(status.action === 'enable' || status.action === 'test') && (
            <div className="notif-status-actions">
              {status.action === 'enable' && (
                <Button variant="primary" size="sm" className="touch-btn" onClick={handleEnable} disabled={enabling}>
                  {enabling ? t('notifications.enabling') : (registeredHere ? t('notifications.reEnable') : t('notifications.enable'))}
                </Button>
              )}
              {status.action === 'test' && (
                <Button variant="outline-primary" size="sm" className="touch-btn" onClick={handleTest} disabled={testing}>
                  {testing ? t('notifications.testing') : t('notifications.testSend')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert variant="success" dismissible onClose={() => setNotice('')}>{notice}</Alert>}

      {/* Shown only where it applies: to everyone would train the team to skip the panel's text,
          and to an already-installed iPhone it reads as «you did it wrong». */}
      {config?.enabled && iosNeedsInstall && (
        <div className="notif-ios">
          <div className="notif-ios-title">{t('notifications.iosTitle')}</div>
          <ol className="notif-ios-steps">
            {t('notifications.iosSteps').map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
      )}

      {config?.enabled && (
        <>
          <div className="notif-section">
            <h6 className="notif-section-title">{t('notifications.typesTitle')}</h6>
            <p className="notif-section-hint">{t('notifications.typesHint')}</p>

            {typeList.map(({ key }) => {
              const label = typeLabel(t, key);
              return (
                <div className="notif-type-row" key={key}>
                  <div>
                    <div className="notif-type-label">{label.label}</div>
                    <div className="notif-type-description">{label.description}</div>
                  </div>
                  <Form.Check
                    type="switch"
                    id={`notif-type-${key}`}
                    aria-label={label.label}
                    // Absence of a preference row means ON — the server resolves that before
                    // answering, so the client never has to know the rule.
                    checked={preferences[key] !== false}
                    onChange={(e) => handleToggle(key, e.target.checked)}
                  />
                </div>
              );
            })}
          </div>

          <div className="notif-section">
            <h6 className="notif-section-title">
              {t('notifications.devicesTitle')}
              {devices.length > 0 && <span className="notif-count">{formatCount(devices.length, language)}</span>}
            </h6>
            <p className="notif-section-hint">{t('notifications.devicesHint')}</p>

            {devices.length === 0 ? (
              <div className="notif-devices-empty">{t('notifications.devicesEmpty')}</div>
            ) : (
              devices.map((device) => {
                const health = deviceHealth(t, device.health);
                const isThis = String(device._id) === String(thisDeviceId);
                return (
                  <div className="notif-device" key={device._id}>
                    <span
                      className={`notif-device-health notif-device-health--${device.health || 'unknown'}`}
                      aria-hidden="true"
                    >
                      {health.icon}
                    </span>

                    <div className="notif-device-body">
                      <div className="notif-device-name">
                        {device.label}
                        {isThis && <span className="notif-device-this">{t('notifications.thisDevice')}</span>}
                      </div>
                      <div className="notif-device-meta">
                        {health.label}
                        {/* An INSTANT — rendered through the shared formatter, never a local one. */}
                        {device.lastSuccessfulAcceptance
                          ? ` · ${t('notifications.lastAccepted')} ${formatRelativeTime(device.lastSuccessfulAcceptance, language)}`
                          : ` · ${t('notifications.neverReceived')}`}
                      </div>
                      {/* D-e: a device is flagged on consecutive FAILURES, never on age — a phone
                          nobody has pushed to in a quiet week is not broken. */}
                      {device.consecutiveFailures > 0 && (
                        <div className="notif-device-meta notif-device-meta--warn">
                          {t('notifications.failing')} ({formatCount(device.consecutiveFailures, language)})
                        </div>
                      )}
                    </div>

                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleRemove(device._id)}
                      disabled={removingId === device._id}
                    >
                      {removingId === device._id ? '...' : t('notifications.remove')}
                    </Button>
                  </div>
                );
              })
            )}

            {/* Available whenever a device exists, not only in the healthy state — proving a device
                is exactly what someone does when they suspect it stopped working. */}
            {devices.length > 0 && status.action !== 'test' && (
              <Button
                variant="outline-primary"
                size="sm"
                className="touch-btn"
                onClick={handleTest}
                disabled={testing}
              >
                {testing ? t('notifications.testing') : t('notifications.testSend')}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationSettingsPanel;
